/**
 * Admin-Seite "Daten-Updates" (v0.4.0): neue Datenstände bei den Quellen,
 * manueller Check, Installieren (Link in den jeweiligen Import-Flow) und Ausblenden.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Loader2, Download, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '../../lib/utils'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

export const UPDATES_QUERY_KEY = ['admin', 'updates'] as const

export interface UpdateFinding {
  id: number
  source_type: string
  source_key: string
  available_year: number
  imported_year: number | null
  title: string
  detail: Record<string, unknown> | null
  status: 'open' | 'installed' | 'dismissed'
  found_at: string
  last_checked_at: string | null
  resolved_at: string | null
}

interface CheckStatus {
  state: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | string
  result?: { findings_new?: number; findings_total_open?: number; errors?: Record<string, string> } | null
}

export async function fetchUpdates(): Promise<UpdateFinding[]> {
  const res = await fetch(`${API_BASE}/admin/updates`)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

const sourceLabels: Record<string, { label: string; color: string }> = {
  gv100: { label: 'GV100', color: 'bg-blue-100 text-blue-700' },
  genesis_table: { label: 'GENESIS', color: 'bg-purple-100 text-purple-700' },
  inkar: { label: 'INKAR', color: 'bg-green-100 text-green-700' },
}

const statusLabels: Record<string, { label: string; color: string }> = {
  open: { label: 'Offen', color: 'bg-yellow-100 text-yellow-700' },
  installed: { label: 'Installiert', color: 'bg-green-100 text-green-700' },
  dismissed: { label: 'Ausgeblendet', color: 'bg-gray-100 text-gray-600' },
}

export function installLink(f: UpdateFinding): string | null {
  switch (f.source_type) {
    case 'gv100':
      return `/admin/import?tab=destatis&year=${f.available_year}`
    case 'genesis_table':
      return `/admin/genesis?code=${encodeURIComponent(f.source_key)}`
    case 'inkar':
      return `/admin/inkar?year=${f.available_year}`
    default:
      return null
  }
}

export function UpdatesPage() {
  const queryClient = useQueryClient()
  const { data: findings, isLoading, error, refetch } = useQuery({
    queryKey: UPDATES_QUERY_KEY,
    queryFn: fetchUpdates,
    staleTime: 0,
  })

  const [checking, setChecking] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false
    return () => {
      cancelled.current = true
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const reload = () => queryClient.invalidateQueries({ queryKey: UPDATES_QUERY_KEY })

  const poll = (jobId: string) => {
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/updates/check/status/${jobId}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const status: CheckStatus = await res.json()
        if (cancelled.current) return
        if (status.state === 'SUCCESS') {
          const r = status.result
          const counts = `${r?.findings_new ?? 0} neu, ${r?.findings_total_open ?? 0} offen.`
          const errs = Object.entries(r?.errors ?? {})
          if (errs.length > 0) {
            setMessage({
              kind: 'warn',
              text: `Prüfung abgeschlossen mit Fehlern: ${errs.map(([k, v]) => `${k}: ${v}`).join(', ')} (${counts})`,
            })
          } else {
            setMessage({ kind: 'ok', text: `Prüfung abgeschlossen: ${counts}` })
          }
          setChecking(false)
          reload()
        } else if (status.state === 'FAILURE') {
          setMessage({ kind: 'error', text: 'Prüfung fehlgeschlagen. Details in den Celery-Logs.' })
          setChecking(false)
          reload()
        } else {
          poll(jobId)
        }
      } catch (e) {
        if (cancelled.current) return
        setMessage({ kind: 'error', text: `Status-Abfrage fehlgeschlagen: ${(e as Error).message}` })
        setChecking(false)
      }
    }, 2000)
  }

  const startCheck = async () => {
    setChecking(true)
    setMessage(null)
    try {
      const res = await fetch(`${API_BASE}/admin/updates/check`, { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const { job_id } = await res.json()
      poll(job_id)
    } catch (e) {
      setMessage({ kind: 'error', text: `Prüfung konnte nicht gestartet werden: ${(e as Error).message}` })
      setChecking(false)
    }
  }

  const dismiss = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE}/admin/updates/${id}/dismiss`, { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await reload()
    } catch (e) {
      setMessage({ kind: 'error', text: `Ausblenden fehlgeschlagen: ${(e as Error).message}` })
    }
  }

  const openCount = findings?.filter((f) => f.status === 'open').length ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daten-Updates</h1>
          <p className="text-gray-500 mt-1">
            Neue Datenstände bei GV100, GENESIS/Regionalstatistik und INKAR
            {findings ? ` (${openCount} offen)` : ''}
          </p>
        </div>
        <button
          onClick={startCheck}
          disabled={checking}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            checking
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          )}
        >
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {checking ? 'Prüfung läuft…' : 'Auf Updates prüfen'}
        </button>
      </div>

      {message && (
        <div
          className={cn(
            'flex items-center gap-2 p-3 rounded-lg border text-sm',
            message.kind === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : message.kind === 'warn'
                ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                : 'bg-red-50 border-red-200 text-red-800'
          )}
        >
          {message.kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-red-50 border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4" />
          Liste konnte nicht geladen werden.
          <button onClick={() => refetch()} className="underline">Erneut versuchen</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade…
          </div>
        ) : !findings || findings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Keine Funde. Alle Datenstände sind aktuell.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Quelle', 'Titel', 'Verfügbar', 'Importiert', 'Gefunden am', 'Status', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {findings.map((f) => {
                const src = sourceLabels[f.source_type] ?? { label: f.source_type, color: 'bg-gray-100 text-gray-700' }
                const st = statusLabels[f.status] ?? { label: f.status, color: 'bg-gray-100 text-gray-700' }
                const link = installLink(f)
                return (
                  <tr key={f.id} className={cn(f.status !== 'open' && 'text-gray-400')}>
                    <td className="px-4 py-3">
                      <span className={cn('text-xs px-2 py-0.5 rounded', src.color)}>{src.label}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{f.title}</td>
                    <td className="px-4 py-3 text-sm">{f.available_year}</td>
                    <td className="px-4 py-3 text-sm">{f.imported_year ?? '–'}</td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(f.found_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn('text-xs px-2 py-0.5 rounded', st.color)}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {f.status === 'open' && (
                        <div className="flex justify-end gap-2">
                          {link && (
                            <Link
                              to={link}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-blue-600 text-white hover:bg-blue-700"
                            >
                              <Download className="w-4 h-4" />
                              Installieren
                            </Link>
                          )}
                          <button
                            onClick={() => dismiss(f.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            <EyeOff className="w-4 h-4" />
                            Ausblenden
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
